import React, { useState, useEffect } from 'react';
import { Tag } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import Modal from '../../component/Modal/Modal';
import NoxTable from '../../component/Table';
import {
  fetchPaymentSubscriptions,
  updatePaymentSubscription,
  subscriptionUpdateSuccess
} from '../../actions/paymentSubcriptionAction';
import InnerHeader from '../../component/InnerHeader';
import Loader from '../../component/Loader/Loader';
import Copy from '../../component/Copy';
import CButton from '../../component/Button';
import CustomDatePicker from '../../component/DatePicker';
import formatDate from '../../helpers/formatDate';
import { useNotification } from '../../providers/notificationsProvider';

const datePickerStyle = {
  display: 'flex',
  width: '100%'
};

const PaymentSubscription = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRowDate, setSelectedRowDate] = useState('');
  const [isSaveEnabled, setSaveEnabled] = useState(true);

  const currentProject = useSelector(({ project }) => project.currentProject);
  const paymentSubscriptions = useSelector(
    ({ paymentSubscription }) => paymentSubscription.payments
  );
  const isLoading = useSelector(({ paymentSubscription }) => paymentSubscription.isLoading);
  const success = useSelector(({ paymentSubscription }) => paymentSubscription.success);

  const dispatch = useDispatch();
  const { notify } = useNotification();

  const mapToTable = (paymentSubscriptions) => {
    const data = [];

    paymentSubscriptions?.forEach(
      ({ _id, userId, productId, nextPaymentDate, trialDate, isActive }, index) => {
        let finalStatus = '-';
        switch (isActive) {
          case true:
            finalStatus = 'Active';
            break;
          case undefined:
            finalStatus = 'Unknown';
            break;
          case false:
            finalStatus = 'Inactive';
            break;
          default:
            break;
        }
        data.push({
          id: _id,
          key: index,
          nextPaymentDate: formatDate(nextPaymentDate),
          productId,
          status: finalStatus,
          trialDate: formatDate(trialDate),
          userId
        });
      }
    );

    return data;
  };

  const columns = [
    {
      dataIndex: 'userId',
      key: 'userId',
      render: (userId) => {
        return <Copy text={userId} />;
      },
      sorter: (a, b) => a.userId.localeCompare(b.userId),
      title: 'User ID'
    },
    {
      dataIndex: 'productId',
      key: 'productId',
      render: (productId) => {
        return <Copy text={productId} />;
      },
      sorter: (a, b) => a.productId.toString().localeCompare(b.productId),
      title: 'Product ID'
    },
    {
      dataIndex: 'nextPaymentDate',
      key: 'nextPaymentDate',
      render: (nextPaymentDate) => {
        return <Copy text={nextPaymentDate} />;
      },
      sorter: (a, b) => new Date(a.nextPaymentDate) - new Date(b.nextPaymentDate),
      title: 'Next Payment Date'
    },
    {
      dataIndex: 'trialDate',
      key: 'trialDate',
      render: (trialDate) => {
        return <Copy text={trialDate} />;
      },
      sorter: (a, b) => new Date(a.trialDate) - new Date(b.trialDate),
      title: 'Trial Date'
    },
    {
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let style = {};
        switch (status) {
          case 'Active':
            style = {
              backgroundColor: 'rgba(65, 204, 156, 0.15)',
              border: 'none',
              color: '#41CC9C',
              width: '70%'
            };
            break;
          case 'Unknown':
            style = {
              backgroundColor: 'rgba(155, 160, 173, 0.15)',
              border: 'none',
              color: '#9BA0AD',
              width: '70%'
            };
            break;
          case 'Inactive':
            style = {
              backgroundColor: 'rgba(255, 71, 108, 0.15)',
              border: 'none',
              color: '#FF476C',
              width: '70%'
            };
            break;
          default:
            break;
        }
        return (
          <Tag key={status} style={style}>
            {status}
          </Tag>
        );
      },
      sorter: (a, b) => a.status.toString().localeCompare(b.status),
      title: 'Status'
    }
  ];

  const onSave = (e) => {
    // eslint-disable-next-line no-restricted-syntax
    for (const index of selectedRowKeys) {
      const item = data[index];

      e.preventDefault();
      setSaveEnabled(false);
      dispatch(
        updatePaymentSubscription(
          item.id,
          { nextPaymentDate: selectedRowDate },
          () => dispatch(fetchPaymentSubscriptions()),
          (message, severity) => notify({ message, severity })
        )
      );
      dispatch(subscriptionUpdateSuccess(false));
      setSelectedRowDate('');
    }

    setSelectedRowKeys([]);
  };

  const selectRowDate = () => {
    // eslint-disable-next-line no-restricted-syntax
    for (const index of selectedRowKeys) {
      const item = data[index];
      setSelectedRowDate(new Date(item?.nextPaymentDate));
    }
  };

  const onModalOpen = () => {
    setSaveEnabled(true);
    selectRowDate();
    dispatch(subscriptionUpdateSuccess(true));
  };

  const onModalCancel = () => {
    dispatch(subscriptionUpdateSuccess(false));
    setSelectedRowDate('');
  };

  const onDateChange = (date) => {
    setSelectedRowDate(date);
  };

  const actions = [];

  if (selectedRowKeys.length) {
    actions.unshift({
      onClick: onModalOpen,
      title: 'Update Next Payment Date',
      type: 'primary'
    });
  }

  useEffect(() => {
    async function load() {
      dispatch(fetchPaymentSubscriptions());
    }

    if (currentProject) {
      load();
    }
  }, [currentProject, dispatch]);

  const data = mapToTable(paymentSubscriptions);

  if (!currentProject) {
    return <Loader />;
  }

  return (
    <div
      style={{
        height: 'calc(100vh - 165px)',
        overflowY: 'scroll'
      }}
    >
      <InnerHeader subtitle={data.length} title="Payment Subscriptions" />
      <NoxTable
        actions={actions}
        columns={columns}
        data={data}
        loading={!isLoading}
        searchBy="userId"
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
      />
      <Modal
        footerActions={
          <>
            <CButton
              htmlType="submit"
              onClick={(e) => {
                dispatch(subscriptionUpdateSuccess(false));
                e.preventDefault();
              }}
              size="large"
              type="secondary"
            >
              Cancel
            </CButton>
            <CButton
              disabled={!isSaveEnabled}
              htmlType="submit"
              onClick={(e) => onSave(e)}
              size="large"
              type="primary"
            >
              Update
            </CButton>
          </>
        }
        handleClose={onModalCancel}
        isModalOpen={success}
        styles={{
          '& .MuiDialogContent-root': {
            paddingBottom: '24px'
          },
          width: '410px'
        }}
        titleContent={<span className="modal-title">Update Next Payment Date</span>}
      >
        <form
          id="new-content-model-form"
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'space-between'
          }}
        >
          <div style={datePickerStyle}>
            <CustomDatePicker
              onChange={onDateChange}
              timePicker={false}
              value={selectedRowDate || ''}
            />
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default PaymentSubscription;
